/**
 * How long the server asked us to wait, and what to do when it did not say.
 *
 * ## Why this exists
 *
 * A 429 or 503 is not an ordinary failure. It is the server saying "I am
 * protecting myself, come back later", and treating it like a normal error is
 * how a protected server gets knocked over anyway: every refused client retries
 * immediately, in unison, and the refusal wave becomes the next arrival wave.
 * The whole point of shedding is lost if the shed clients come straight back.
 *
 * Both of our defences produce these. Cloudflare's rate limiting `block` action
 * answers 429. The API's own event-loop pressure shed refuses sockets, and any
 * origin overload surfaces as 503. This module is the client half of both.
 *
 * ## Reading Retry-After
 *
 * RFC 9110 allows two forms and we must accept both: delta-seconds
 * (`Retry-After: 30`) and an HTTP-date (`Retry-After: Wed, 21 Oct 2026 07:28:00
 * GMT`). Cloudflare sends the numeric form; origins and proxies vary, and a
 * client that understands only one silently falls back to guessing against the
 * other.
 *
 * ## Why the value is always jittered, even when the server gave one
 *
 * This is the part that is easy to get wrong. If ten thousand clients are all
 * told "retry after 30 seconds", they all retry at exactly thirty seconds and
 * reproduce the herd the header existed to prevent — just half a minute later,
 * and now perfectly synchronised rather than merely correlated.
 *
 * `Retry-After` is therefore treated as a FLOOR, not an appointment: wait at
 * least that long, then a random extra span on top. Full jitter over the extra
 * span, for the same reason `socket-options.ts` uses it — partial jitter still
 * leaves a dense cluster, and a cluster is the defect.
 */

/** Statuses that mean "the server is protecting itself", not "your request was bad". */
export function isBackpressure(status: number): boolean {
  return status === 429 || status === 503;
}

/**
 * Cap on any server-supplied wait.
 *
 * A misconfigured rule or a hostile intermediary can send `Retry-After: 86400`,
 * and a mobile client that honours that literally is bricked for a day with no
 * way for the user to understand why. The server may ask us to wait; it may not
 * ask us to stop existing. Five minutes is far beyond any real burst — ours are
 * measured in tens of seconds — while staying inside a session a user might
 * plausibly still be in.
 */
export const MAX_RETRY_AFTER_MS = 5 * 60_000;

/** Used when the server refuses without saying for how long. */
export const DEFAULT_RETRY_AFTER_MS = 5_000;

/** Random extra span added on top of the floor. See the jitter note above. */
export const RETRY_JITTER_MS = 5_000;

/**
 * Parse a `Retry-After` header into milliseconds, or `null` if absent/unusable.
 *
 * `now` is injectable so the HTTP-date branch is testable without freezing the
 * clock — a date-form header is only meaningful relative to some present.
 */
export function parseRetryAfter(
  header: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (header === null || header === undefined) return null;
  const raw = header.trim();
  if (raw === "") return null;

  // delta-seconds. Anchored so "30x" is rejected rather than read as 30 —
  // a header we half-understand is worse than one we ignore.
  if (/^\d+$/.test(raw)) {
    const seconds = Number(raw);
    if (!Number.isFinite(seconds)) return null;
    return Math.min(seconds * 1000, MAX_RETRY_AFTER_MS);
  }

  // HTTP-date.
  const at = Date.parse(raw);
  if (Number.isNaN(at)) return null;
  // A date already in the past means "you may retry now", not a negative wait.
  const delta = at - now;
  if (delta <= 0) return 0;
  return Math.min(delta, MAX_RETRY_AFTER_MS);
}

/**
 * How long to actually wait before retrying, given what the server ASKED for.
 *
 * Takes the parsed number, not the header, and that split is the point.
 *
 * Parsing is a transport fact: it is what the server said. Choosing when to
 * come back is a scheduling decision, and it belongs to whoever is doing the
 * scheduling. Collapsing the two — jittering at the moment of parsing and
 * storing the result as though it were the server's word — produced a value
 * that no later layer could interpret, because it could no longer tell the
 * server's statement from our own padding. Concretely, with the default and
 * the jitter baked in: a response with NO header became "wait 5-10s", which is
 * indistinguishable from a server that asked for 7; and `Retry-After: 9`
 * became 9-14s, so a consumer with a 10s policy threshold applied it or not
 * depending on a coin flip. `ApiError.retryAfterMs` now carries the raw ask.
 *
 * The server's number stays a floor and never an appointment — see above. When
 * it gives none we still wait, because "refused with no advice" is not
 * permission to retry immediately.
 */
export function backpressureDelayMs(
  askedMs: number | null | undefined,
  random: () => number = Math.random,
): number {
  const floor =
    typeof askedMs === "number" && Number.isFinite(askedMs) && askedMs > 0
      ? Math.min(askedMs, MAX_RETRY_AFTER_MS)
      : DEFAULT_RETRY_AFTER_MS;
  return Math.min(floor + random() * RETRY_JITTER_MS, MAX_RETRY_AFTER_MS);
}
